import { Card, CardContent } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { Skeleton } from '@/common/ui/skeleton'
import useQueryGetPlayerCommission from '@/modules/manage-player/api/hooks/useQueryGetPlayerCommission'
import {
	formatGameTypeToThai,
	formatCreditsAmount,
} from '../../utils/gameTypeLabels'
import { format, parseISO } from 'date-fns'
import { th } from 'date-fns/locale'
import { Clock } from 'lucide-react'
import { cn } from '@/common/lib/utils'

type PlayerCommissionInfoProps = {
	playerId: string
	/** When true, omit outer card and page title (use inside Dialog). */
	embedded?: boolean
}

const shellClass = (embedded: boolean) =>
	cn(embedded ? 'space-y-4' : 'rounded-lg border bg-card p-4 shadow-sm sm:p-6')

export default function PlayerCommissionInfo({
	playerId,
	embedded = false,
}: PlayerCommissionInfoProps) {
	const { data, isLoading, isError } = useQueryGetPlayerCommission(playerId)

	if (isLoading) {
		return (
			<div className={shellClass(embedded)}>
				<Skeleton className="mb-4 h-8 w-48" />
				<div className="space-y-3">
					<Skeleton className="h-24 w-full" />
					<Skeleton className="h-24 w-full" />
				</div>
			</div>
		)
	}

	if (isError || !data) {
		return (
			<div className={shellClass(embedded)}>
				{!embedded && (
					<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
						คอมมิชชั่น (คืนยอดเล่น)
					</h2>
				)}
				<p className="mt-2 text-sm text-muted-foreground">
					ไม่สามารถโหลดข้อมูลได้ หรือสมาชิกไม่มีคอมมิชชั่นตามยศ
				</p>
			</div>
		)
	}

	const creditEntries = Object.entries(data.creditRatio ?? {})

	return (
		<div className={shellClass(embedded)}>
			{embedded ? (
				<div className="flex justify-end">
					<Badge variant={data.isActive ? 'default' : 'secondary'}>
						{data.isActive ? 'เปิดใช้งาน' : 'ปิด'}
					</Badge>
				</div>
			) : (
				<div className="mb-4 flex flex-col gap-2 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
							คอมมิชชั่น (คืนยอดเล่น)
						</h2>
						<p className="text-sm text-muted-foreground sm:text-base">
							ยอดคอมมิชชั่นเดือนนี้แยกตามประเภทเกม
						</p>
					</div>
					<Badge variant={data.isActive ? 'default' : 'secondary'}>
						{data.isActive ? 'เปิดใช้งาน' : 'ปิด'}
					</Badge>
				</div>
			)}

			<div className="mb-4 rounded-lg border border-primary/10 bg-primary/5 p-4 text-center">
				<p className="text-sm text-muted-foreground">รวมคอมมิชชั่นเดือนนี้</p>
				<p className="text-2xl font-bold text-primary">
					{formatCreditsAmount(data.totalAmount)} เครดิต
				</p>
			</div>

			{data.isMultiplierActive &&
				data.multiplier != null &&
				data.multiplier > 1 &&
				data.multiplierEndDate && (
					<div className="mb-4 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
						<Clock className="h-4 w-4 shrink-0 text-primary" />
						<span>
							คูณคอมมิชชั่น <strong>{data.multiplier}x</strong> ถึง{' '}
							{format(parseISO(data.multiplierEndDate), 'd MMM yyyy HH:mm', {
								locale: th,
							})}
						</span>
					</div>
				)}

			<p className="mb-2 text-sm text-muted-foreground">
				เทิร์นที่ต้องทำก่อนโอน: {data.turnoverRound} เท่า
			</p>

			<h3 className="mb-3 text-lg font-semibold">ตามประเภทเกม</h3>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				{creditEntries.map(([gameType, ratio]) => {
					const pct = (ratio.creditRatio ?? 0) * 100
					const earned = data.gameTypeAmounts?.[gameType] ?? 0
					return (
						<Card key={gameType}>
							<CardContent className="p-4">
								<div className="flex items-start justify-between gap-2">
									<div>
										<p className="font-medium">
											{formatGameTypeToThai(gameType)}
										</p>
										<p className="text-xs text-muted-foreground">
											ได้รับแล้ว {formatCreditsAmount(earned)} เครดิต
										</p>
									</div>
									<div className="text-right">
										<p className="text-lg font-bold text-primary">
											{pct.toLocaleString('th-TH', {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})}
											%
										</p>
										{ratio.maxCreditAmount != null && (
											<p className="text-xs text-muted-foreground">
												สูงสุด {formatCreditsAmount(ratio.maxCreditAmount)} /
												เดือน
											</p>
										)}
									</div>
								</div>
							</CardContent>
						</Card>
					)
				})}
			</div>
		</div>
	)
}
